import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Building2, 
  FileText, 
  CreditCard, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  Plus,
  Edit,
  Trash2,
  CalendarIcon,
  Receipt,
  FileSignature,
  TrendingUp,
  Euro,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

// Swiss cantons
const CANTONS = [
  'AG', 'AI', 'AR', 'BE', 'BL', 'BS', 'FR', 'GE', 'GL', 'GR',
  'JU', 'LU', 'NE', 'NW', 'OW', 'SG', 'SH', 'SO', 'SZ', 'TG',
  'TI', 'UR', 'VD', 'VS', 'ZG', 'ZH'
];

const PAYMENT_METHODS = [
  { value: 'bonifico', label: 'Bonifico Bancario' },
  { value: 'lsv', label: 'LSV+' },
  { value: 'ebill', label: 'eBill' }
];

const PLAN_TYPES = [
  { value: 'mensile', label: 'Mensile' },
  { value: 'annuale', label: 'Annuale' },
  { value: 'personalizzato', label: 'Personalizzato' }
];

const INVOICE_TYPES = [
  { value: 'abbonamento', label: 'Abbonamento' },
  { value: 'servizio', label: 'Servizio Extra' },
  { value: 'export_dati', label: 'Export Dati' },
  { value: 'altro', label: 'Altro' }
];

export const BillingPage = () => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('dati-fiscali');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data states
  const [stats, setStats] = useState({});
  const [structures, setStructures] = useState([]);
  const [billingInfoList, setBillingInfoList] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  
  // Dialog states
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  
  // Form states
  const [billingForm, setBillingForm] = useState({
    legal_name: '',
    address: { street: '', zip_code: '', city: '', canton: '' },
    ide_number: '',
    vat_number: '',
    iban: '',
    billing_email: '',
    billing_contact_name: '',
    billing_contact_phone: '',
    payment_method: 'bonifico',
    notes: ''
  });
  
  const [subscriptionForm, setSubscriptionForm] = useState({
    structure_id: '',
    plan_type: 'mensile',
    start_date: null,
    end_date: null,
    monthly_amount: 0,
    annual_amount: 0,
    is_active: true,
    notes: ''
  });
  
  const [invoiceForm, setInvoiceForm] = useState({
    structure_id: '',
    invoice_type: 'abbonamento',
    items: [{ description: '', quantity: 1, unit_price: 0, vat_rate: 7.7 }],
    due_date: null,
    notes: ''
  });

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, structuresRes, billingRes, subsRes, invoicesRes] = await Promise.all([
        api.get('/billing/stats'),
        api.get('/structures'),
        api.get('/billing/info'),
        api.get('/billing/subscriptions'),
        api.get('/billing/invoices')
      ]);
      
      setStats(statsRes.data);
      setStructures(structuresRes.data);
      setBillingInfoList(billingRes.data);
      setSubscriptions(subsRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Open billing edit dialog
  const openBillingDialog = async (structureId) => {
    try {
      const res = await api.get(`/billing/info/${structureId}`);
      setBillingForm({
        legal_name: res.data.legal_name || '',
        address: res.data.address || { street: '', zip_code: '', city: '', canton: '' },
        ide_number: res.data.ide_number || '',
        vat_number: res.data.vat_number || '',
        iban: res.data.iban || '',
        billing_email: res.data.billing_email || '',
        billing_contact_name: res.data.billing_contact_name || '',
        billing_contact_phone: res.data.billing_contact_phone || '',
        payment_method: res.data.payment_method || 'bonifico',
        notes: res.data.notes || ''
      });
      setSelectedStructure(structures.find(s => s.id === structureId));
      setBillingDialogOpen(true);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati fiscali');
    }
  };

  // Save billing info
  const saveBillingInfo = async () => {
    if (!selectedStructure) return;
    try {
      await api.put(`/billing/info/${selectedStructure.id}`, billingForm);
      toast.success('Dati fiscali salvati');
      setBillingDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  // Open subscription dialog
  const openSubscriptionDialog = (subscription = null) => {
    if (subscription) {
      setSelectedSubscription(subscription);
      setSubscriptionForm({
        structure_id: subscription.structure_id,
        plan_type: subscription.plan_type,
        start_date: subscription.start_date ? parseISO(subscription.start_date) : null,
        end_date: subscription.end_date ? parseISO(subscription.end_date) : null,
        monthly_amount: subscription.monthly_amount || 0,
        annual_amount: subscription.annual_amount || 0,
        is_active: subscription.is_active,
        notes: subscription.notes || ''
      });
    } else {
      setSelectedSubscription(null);
      setSubscriptionForm({
        structure_id: '',
        plan_type: 'mensile',
        start_date: new Date(),
        end_date: null,
        monthly_amount: 0,
        annual_amount: 0,
        is_active: true,
        notes: ''
      });
    }
    setSubscriptionDialogOpen(true);
  };

  // Save subscription
  const saveSubscription = async () => {
    try {
      const data = {
        ...subscriptionForm,
        start_date: subscriptionForm.start_date ? format(subscriptionForm.start_date, 'yyyy-MM-dd') : null,
        end_date: subscriptionForm.end_date ? format(subscriptionForm.end_date, 'yyyy-MM-dd') : null
      };
      
      if (selectedSubscription) {
        await api.put(`/billing/subscriptions/${subscriptionForm.structure_id}`, data);
        toast.success('Abbonamento aggiornato');
      } else {
        await api.post('/billing/subscriptions', data);
        toast.success('Abbonamento creato');
      }
      setSubscriptionDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  // Open invoice dialog
  const openInvoiceDialog = () => {
    setInvoiceForm({
      structure_id: '',
      invoice_type: 'abbonamento',
      items: [{ description: '', quantity: 1, unit_price: 0, vat_rate: 7.7 }],
      due_date: new Date(new Date().setDate(new Date().getDate() + 30)),
      notes: ''
    });
    setInvoiceDialogOpen(true);
  };

  // Add invoice item
  const addInvoiceItem = () => {
    setInvoiceForm(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit_price: 0, vat_rate: 7.7 }]
    }));
  };

  // Update invoice item
  const updateInvoiceItem = (index, field, value) => {
    setInvoiceForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  // Remove invoice item
  const removeInvoiceItem = (index) => {
    if (invoiceForm.items.length === 1) return;
    setInvoiceForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Calculate invoice totals
  const calculateInvoiceTotals = () => {
    const subtotal = invoiceForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const vat = invoiceForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price * (item.vat_rate / 100)), 0);
    return { subtotal, vat, total: subtotal + vat };
  };

  // Save invoice
  const saveInvoice = async () => {
    try {
      const data = {
        ...invoiceForm,
        due_date: invoiceForm.due_date ? format(invoiceForm.due_date, 'yyyy-MM-dd') : null
      };
      
      await api.post('/billing/invoices', data);
      toast.success('Fattura creata');
      setInvoiceDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  // Update invoice status
  const updateInvoiceStatus = async (invoiceId, status) => {
    try {
      await api.put(`/billing/invoices/${invoiceId}`, { status });
      toast.success('Stato aggiornato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  // Filter structures based on search
  const filteredStructures = structures.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get billing info for a structure
  const getBillingInfo = (structureId) => {
    return billingInfoList.find(b => b.structure_id === structureId) || {};
  };

  // Get subscription for a structure
  const getSubscription = (structureId) => {
    return subscriptions.find(s => s.structure_id === structureId);
  };

  // Structures without subscription
  const structuresWithoutSubscription = structures.filter(s => !getSubscription(s.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Gestione Fatturazione</h1>
        <p className="text-slate-500">Dati fiscali, abbonamenti e fatture delle strutture</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Card>
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg shrink-0">
                <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-700" />
              </div>
              <div className="min-w-0 w-full">
                <p className="text-lg sm:text-2xl font-bold">{stats.total_structures || 0}</p>
                <p className="text-[10px] sm:text-sm text-slate-500">Strutture</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg shrink-0">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-700" />
              </div>
              <div className="min-w-0 w-full">
                <p className="text-lg sm:text-2xl font-bold">{stats.complete_billing || 0}</p>
                <p className="text-[10px] sm:text-sm text-slate-500">Completi</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-amber-100 rounded-lg shrink-0">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-700" />
              </div>
              <div className="min-w-0 w-full">
                <p className="text-lg sm:text-2xl font-bold">{stats.expiring_subscriptions || 0}</p>
                <p className="text-[10px] sm:text-sm text-slate-500">Scadenza</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg shrink-0">
                <Euro className="w-4 h-4 sm:w-5 sm:h-5 text-purple-700" />
              </div>
              <div className="min-w-0 w-full">
                <p className="text-base sm:text-2xl font-bold">CHF {(stats.total_pending_amount || 0).toFixed(0)}</p>
                <p className="text-[10px] sm:text-sm text-slate-500">Incassare</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 max-w-2xl h-auto">
          <TabsTrigger value="dati-fiscali" className="flex flex-col sm:flex-row items-center gap-0.5 sm:gap-2 py-2 px-1 sm:px-3 text-[10px] sm:text-sm">
            <FileText className="w-4 h-4 shrink-0" />
            <span className="hidden xs:inline sm:inline">Fiscali</span>
          </TabsTrigger>
          <TabsTrigger value="abbonamenti" className="flex flex-col sm:flex-row items-center gap-0.5 sm:gap-2 py-2 px-1 sm:px-3 text-[10px] sm:text-sm">
            <CreditCard className="w-4 h-4 shrink-0" />
            <span className="hidden xs:inline sm:inline">Abbon.</span>
          </TabsTrigger>
          <TabsTrigger value="fatture" className="flex flex-col sm:flex-row items-center gap-0.5 sm:gap-2 py-2 px-1 sm:px-3 text-[10px] sm:text-sm">
            <Receipt className="w-4 h-4 shrink-0" />
            <span className="hidden xs:inline sm:inline">Fatture</span>
          </TabsTrigger>
          <TabsTrigger value="contratti" className="flex flex-col sm:flex-row items-center gap-0.5 sm:gap-2 py-2 px-1 sm:px-3 text-[10px] sm:text-sm">
            <FileSignature className="w-4 h-4 shrink-0" />
            <span className="hidden xs:inline sm:inline">Contratti</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Dati Fiscali */}
        <TabsContent value="dati-fiscali" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Dati Fiscali Strutture</CardTitle>
                  <CardDescription>Anagrafica completa per la fatturazione</CardDescription>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Cerca struttura..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filteredStructures.map(structure => {
                  const billing = getBillingInfo(structure.id);
                  const isComplete = billing.is_complete;
                  
                  return (
                    <div 
                      key={structure.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${isComplete ? 'bg-green-100' : 'bg-amber-100'}`}>
                          <Building2 className={`w-5 h-5 ${isComplete ? 'text-green-700' : 'text-amber-700'}`} />
                        </div>
                        <div>
                          <p className="font-medium">{structure.name}</p>
                          <p className="text-sm text-slate-500">
                            {billing.legal_name || 'Ragione sociale non compilata'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={isComplete ? 'default' : 'secondary'} className={isComplete ? 'bg-green-100 text-green-700' : ''}>
                          {isComplete ? 'Completo' : 'Incompleto'}
                        </Badge>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openBillingDialog(structure.id)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Modifica
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Abbonamenti */}
        <TabsContent value="abbonamenti" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Abbonamenti Attivi</CardTitle>
                  <CardDescription>Gestione durata e scadenze abbonamenti</CardDescription>
                </div>
                {structuresWithoutSubscription.length > 0 && (
                  <Button onClick={() => openSubscriptionDialog()} className="bg-blue-700 hover:bg-blue-800">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Abbonamento
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {subscriptions.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Nessun abbonamento configurato</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => openSubscriptionDialog()}
                    >
                      Crea il primo abbonamento
                    </Button>
                  </div>
                ) : (
                  subscriptions.map(sub => {
                    const statusColors = {
                      attivo: 'bg-green-100 text-green-700',
                      in_scadenza: 'bg-amber-100 text-amber-700',
                      scaduto: 'bg-red-100 text-red-700',
                      disattivo: 'bg-slate-100 text-slate-700'
                    };
                    
                    return (
                      <div 
                        key={sub.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${statusColors[sub.status]?.replace('text-', 'bg-').replace('700', '100') || 'bg-slate-100'}`}>
                            <CreditCard className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium">{sub.structure_name}</p>
                            <p className="text-sm text-slate-500">
                              {sub.plan_type === 'mensile' ? 'Mensile' : sub.plan_type === 'annuale' ? 'Annuale' : 'Personalizzato'}
                              {sub.monthly_amount > 0 && ` • CHF ${sub.monthly_amount}/mese`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {sub.end_date && (
                            <div className="text-right">
                              <p className="text-sm font-medium">
                                {sub.days_remaining !== null && sub.days_remaining >= 0 
                                  ? `${sub.days_remaining} giorni` 
                                  : 'Scaduto'}
                              </p>
                              <p className="text-xs text-slate-500">
                                Scade: {format(parseISO(sub.end_date), 'dd/MM/yyyy', { locale: it })}
                              </p>
                            </div>
                          )}
                          <Badge className={statusColors[sub.status]}>
                            {sub.status === 'attivo' ? 'Attivo' : 
                             sub.status === 'in_scadenza' ? 'In Scadenza' :
                             sub.status === 'scaduto' ? 'Scaduto' : 'Disattivo'}
                          </Badge>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openSubscriptionDialog(sub)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Fatture */}
        <TabsContent value="fatture" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Fatture Emesse</CardTitle>
                  <CardDescription>Storico e emissione nuove fatture</CardDescription>
                </div>
                <Button onClick={openInvoiceDialog} className="bg-blue-700 hover:bg-blue-800">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuova Fattura
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nessuna fattura emessa</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invoices.map(invoice => {
                    const statusColors = {
                      bozza: 'bg-slate-100 text-slate-700',
                      emessa: 'bg-blue-100 text-blue-700',
                      pagata: 'bg-green-100 text-green-700',
                      annullata: 'bg-red-100 text-red-700'
                    };
                    
                    return (
                      <div 
                        key={invoice.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <Receipt className="w-5 h-5 text-purple-700" />
                          </div>
                          <div>
                            <p className="font-medium">{invoice.invoice_number}</p>
                            <p className="text-sm text-slate-500">
                              {invoice.structure_name} • {INVOICE_TYPES.find(t => t.value === invoice.invoice_type)?.label}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-medium">CHF {invoice.total.toFixed(2)}</p>
                            <p className="text-xs text-slate-500">
                              Scadenza: {format(parseISO(invoice.due_date), 'dd/MM/yyyy', { locale: it })}
                            </p>
                          </div>
                          <Badge className={statusColors[invoice.status]}>
                            {invoice.status === 'bozza' ? 'Bozza' :
                             invoice.status === 'emessa' ? 'Emessa' :
                             invoice.status === 'pagata' ? 'Pagata' : 'Annullata'}
                          </Badge>
                          {invoice.status === 'bozza' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => updateInvoiceStatus(invoice.id, 'emessa')}
                            >
                              Emetti
                            </Button>
                          )}
                          {invoice.status === 'emessa' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-green-700"
                              onClick={() => updateInvoiceStatus(invoice.id, 'pagata')}
                            >
                              Segna Pagata
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Contratti */}
        <TabsContent value="contratti" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Contratti e Documenti</CardTitle>
              <CardDescription>Gestione contratti privacy, GDPR e obblighi contrattuali</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-slate-500">
                <FileSignature className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium mb-2">Funzionalità in Arrivo</p>
                <p className="text-sm max-w-md mx-auto">
                  Questa sezione permetterà di gestire contratti privacy, GDPR e altri documenti contrattuali 
                  da inviare alle strutture via email.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Billing Info Dialog */}
      <Dialog open={billingDialogOpen} onOpenChange={setBillingDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dati Fiscali - {selectedStructure?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Ragione Sociale *</Label>
              <Input
                value={billingForm.legal_name}
                onChange={(e) => setBillingForm({ ...billingForm, legal_name: e.target.value })}
                placeholder="Nome legale completo"
              />
            </div>
            
            <div className="col-span-2">
              <Label>Indirizzo</Label>
              <Input
                value={billingForm.address.street}
                onChange={(e) => setBillingForm({ 
                  ...billingForm, 
                  address: { ...billingForm.address, street: e.target.value }
                })}
                placeholder="Via e numero civico"
              />
            </div>
            
            <div>
              <Label>CAP</Label>
              <Input
                value={billingForm.address.zip_code}
                onChange={(e) => setBillingForm({ 
                  ...billingForm, 
                  address: { ...billingForm.address, zip_code: e.target.value }
                })}
                placeholder="Es: 6900"
              />
            </div>
            
            <div>
              <Label>Città *</Label>
              <Input
                value={billingForm.address.city}
                onChange={(e) => setBillingForm({ 
                  ...billingForm, 
                  address: { ...billingForm.address, city: e.target.value }
                })}
                placeholder="Es: Lugano"
              />
            </div>
            
            <div>
              <Label>Cantone</Label>
              <Select
                value={billingForm.address.canton}
                onValueChange={(v) => setBillingForm({ 
                  ...billingForm, 
                  address: { ...billingForm.address, canton: v }
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona cantone" />
                </SelectTrigger>
                <SelectContent>
                  {CANTONS.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Numero IDE *</Label>
              <Input
                value={billingForm.ide_number}
                onChange={(e) => setBillingForm({ ...billingForm, ide_number: e.target.value })}
                placeholder="CHE-XXX.XXX.XXX"
              />
            </div>
            
            <div>
              <Label>Numero IVA/MWST</Label>
              <Input
                value={billingForm.vat_number}
                onChange={(e) => setBillingForm({ ...billingForm, vat_number: e.target.value })}
                placeholder="Se soggetto IVA"
              />
            </div>
            
            <div>
              <Label>IBAN *</Label>
              <Input
                value={billingForm.iban}
                onChange={(e) => setBillingForm({ ...billingForm, iban: e.target.value })}
                placeholder="CH..."
              />
            </div>
            
            <div>
              <Label>Email Fatturazione *</Label>
              <Input
                type="email"
                value={billingForm.billing_email}
                onChange={(e) => setBillingForm({ ...billingForm, billing_email: e.target.value })}
                placeholder="fatturazione@esempio.ch"
              />
            </div>
            
            <div>
              <Label>Referente Fatturazione</Label>
              <Input
                value={billingForm.billing_contact_name}
                onChange={(e) => setBillingForm({ ...billingForm, billing_contact_name: e.target.value })}
                placeholder="Nome e cognome"
              />
            </div>
            
            <div>
              <Label>Telefono Referente</Label>
              <Input
                value={billingForm.billing_contact_phone}
                onChange={(e) => setBillingForm({ ...billingForm, billing_contact_phone: e.target.value })}
                placeholder="+41..."
              />
            </div>
            
            <div>
              <Label>Modalità Pagamento</Label>
              <Select
                value={billingForm.payment_method}
                onValueChange={(v) => setBillingForm({ ...billingForm, payment_method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="col-span-2">
              <Label>Note</Label>
              <Textarea
                value={billingForm.notes}
                onChange={(e) => setBillingForm({ ...billingForm, notes: e.target.value })}
                placeholder="Note aggiuntive..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBillingDialogOpen(false)}>Annulla</Button>
            <Button onClick={saveBillingInfo} className="bg-blue-700 hover:bg-blue-800">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Dialog */}
      <Dialog open={subscriptionDialogOpen} onOpenChange={setSubscriptionDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedSubscription ? 'Modifica Abbonamento' : 'Nuovo Abbonamento'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!selectedSubscription && (
              <div>
                <Label>Struttura *</Label>
                <Select
                  value={subscriptionForm.structure_id}
                  onValueChange={(v) => setSubscriptionForm({ ...subscriptionForm, structure_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona struttura" />
                  </SelectTrigger>
                  <SelectContent>
                    {structuresWithoutSubscription.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Tipo Piano</Label>
              <Select
                value={subscriptionForm.plan_type}
                onValueChange={(v) => setSubscriptionForm({ ...subscriptionForm, plan_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAN_TYPES.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Inizio *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {subscriptionForm.start_date 
                        ? format(subscriptionForm.start_date, 'dd/MM/yyyy', { locale: it })
                        : 'Seleziona data'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={subscriptionForm.start_date}
                      onSelect={(d) => setSubscriptionForm({ ...subscriptionForm, start_date: d })}
                      locale={it}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div>
                <Label>Data Fine</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {subscriptionForm.end_date 
                        ? format(subscriptionForm.end_date, 'dd/MM/yyyy', { locale: it })
                        : 'Indeterminato'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={subscriptionForm.end_date}
                      onSelect={(d) => setSubscriptionForm({ ...subscriptionForm, end_date: d })}
                      locale={it}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Importo Mensile (CHF)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={subscriptionForm.monthly_amount}
                  onChange={(e) => setSubscriptionForm({ ...subscriptionForm, monthly_amount: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Importo Annuale (CHF)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={subscriptionForm.annual_amount}
                  onChange={(e) => setSubscriptionForm({ ...subscriptionForm, annual_amount: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            
            <div>
              <Label>Note</Label>
              <Textarea
                value={subscriptionForm.notes}
                onChange={(e) => setSubscriptionForm({ ...subscriptionForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubscriptionDialogOpen(false)}>Annulla</Button>
            <Button onClick={saveSubscription} className="bg-blue-700 hover:bg-blue-800">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova Fattura</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Struttura *</Label>
                <Select
                  value={invoiceForm.structure_id}
                  onValueChange={(v) => setInvoiceForm({ ...invoiceForm, structure_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona struttura" />
                  </SelectTrigger>
                  <SelectContent>
                    {structures.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Tipo Fattura</Label>
                <Select
                  value={invoiceForm.invoice_type}
                  onValueChange={(v) => setInvoiceForm({ ...invoiceForm, invoice_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INVOICE_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Voci Fattura</Label>
                <Button variant="outline" size="sm" onClick={addInvoiceItem}>
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              </div>
              
              {invoiceForm.items.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 mb-2">
                  <div className="col-span-5">
                    <Input
                      placeholder="Descrizione"
                      value={item.description}
                      onChange={(e) => updateInvoiceItem(index, 'description', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      placeholder="Qtà"
                      value={item.quantity}
                      onChange={(e) => updateInvoiceItem(index, 'quantity', parseInt(e.target.value) || 1)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Prezzo"
                      value={item.unit_price}
                      onChange={(e) => updateInvoiceItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="IVA %"
                      value={item.vat_rate}
                      onChange={(e) => updateInvoiceItem(index, 'vat_rate', parseFloat(e.target.value) || 7.7)}
                    />
                  </div>
                  <div className="col-span-1">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => removeInvoiceItem(index)}
                      disabled={invoiceForm.items.length === 1}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              
              <div className="mt-4 p-3 bg-slate-50 rounded-lg text-right">
                <p className="text-sm">Subtotale: CHF {calculateInvoiceTotals().subtotal.toFixed(2)}</p>
                <p className="text-sm">IVA: CHF {calculateInvoiceTotals().vat.toFixed(2)}</p>
                <p className="font-bold">Totale: CHF {calculateInvoiceTotals().total.toFixed(2)}</p>
              </div>
            </div>
            
            <div>
              <Label>Data Scadenza</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {invoiceForm.due_date 
                      ? format(invoiceForm.due_date, 'dd/MM/yyyy', { locale: it })
                      : 'Seleziona data'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={invoiceForm.due_date}
                    onSelect={(d) => setInvoiceForm({ ...invoiceForm, due_date: d })}
                    locale={it}
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div>
              <Label>Note</Label>
              <Textarea
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>Annulla</Button>
            <Button onClick={saveInvoice} className="bg-blue-700 hover:bg-blue-800">Crea Fattura</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingPage;
